/**
 * Small helpers for turning raw phone / email strings into clickable
 * tel:/mailto: links the way Google's UI does — same behaviour on every
 * platform: desktop hands off to the user's default dialer/mail client,
 * mobile triggers the OS dialer/mail app.
 *
 * We add tel:/mailto:, we don't *call* — no PII leaves the device, no third
 * party gets the phone number, the dialer just opens with it pre-filled.
 *
 * Phone normalization: strip everything except digits + leading "+", and
 * default a leading "+1" for US numbers (10 digits) since that's the only
 * locale the product currently serves. Foreign numbers that already include
 * a "+" pass through untouched. If we can't make sense of the input we
 * return null so the caller renders plain text instead of a broken link.
 */

const US_AREA_CODES = 10; // 10-digit length without country prefix

export function toTelHref(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Preserve a leading "+" if present, then strip everything non-digit.
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return null;
  if (hasPlus) return `tel:+${digits}`;
  if (digits.length === US_AREA_CODES) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `tel:+${digits}`;
  // Other lengths (extensions, short codes, malformed) — punt to a bare
  // tel: with the raw digits. Dialers tolerate this; the worst case is the
  // user manually corrects it.
  return `tel:${digits}`;
}

/**
 * Pretty-print a US phone for display while keeping the underlying tel:
 * link normalized. Falls back to the raw input if it doesn't fit the
 * US 10-digit pattern.
 */
export function formatPhoneDisplay(raw: string | null | undefined): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return trimmed;
}

export function toMailtoHref(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Loose check — anything with an "@" and a "." past it is worth handing to
  // the mail client. Strict RFC-5322 validation is the dialer's problem.
  if (!/@.*\./.test(trimmed)) return null;
  return `mailto:${encodeURIComponent(trimmed).replace(/%40/g, "@")}`;
}
